/**
 * 星舰广播 API
 * 调用本地 NestJS 后端（vite proxy: /api -> localhost:3856 -> /api/v1/*）
 */
import axios from 'axios';
import { message } from 'antd';
import { useUserStore } from '@/stores/userStore';

export type BroadcastTargetType = 'all' | 'users';
export type BroadcastPriority = 'normal' | 'important' | 'critical';
export type BroadcastStatus = 'active' | 'revoked';

export interface Broadcast {
  id: string;
  title: string;
  contentMd: string;
  senderId: string;
  senderName: string | null;
  targetType: BroadcastTargetType;
  targetUserIds: string | null;
  priority: BroadcastPriority;
  expireAt: string | null;
  status: BroadcastStatus;
  createdAt: string;
  updatedAt: string;
}

export interface BroadcastWithStats extends Broadcast {
  readCount: number;
  /** -1 表示全员广播未展开统计 */
  targetCount: number;
}

export interface MyBroadcastItem {
  broadcast: Broadcast;
  readAt: string | null;
}

export interface CreateBroadcastPayload {
  title: string;
  contentMd: string;
  targetType: BroadcastTargetType;
  targetUserIds?: string[];
  priority?: BroadcastPriority;
  expireAt?: string | null;
  senderName?: string;
}

export interface UpdateBroadcastPayload {
  title?: string;
  contentMd?: string;
  priority?: BroadcastPriority;
  expireAt?: string | null;
}

// 走 vite proxy: /api -> localhost:3856 -> /api/v1
const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// 自动注入 X-User-Identifier
apiClient.interceptors.request.use((config) => {
  const userInfo = useUserStore.getState().userInfo;
  if (userInfo?.id) {
    config.headers = config.headers || {};
    (config.headers as any)['X-User-Identifier'] = userInfo.id;
  }
  return config;
});

apiClient.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const data = error?.response?.data;
    const status = error?.response?.status;
    if (status === 403) {
      message.error(data?.message || '权限不足');
    } else if (status === 401) {
      message.error(data?.message || '请先登录');
    } else if (status >= 500) {
      message.error('广播服务异常，请稍后再试');
    }
    return Promise.reject(error);
  },
);

// ========== 管理员接口 ==========
export const broadcastCreate = (payload: CreateBroadcastPayload) =>
  apiClient.post<Broadcast>('/broadcasts', payload).then((r) => r.data);

export const broadcastList = () =>
  apiClient.get<BroadcastWithStats[]>('/broadcasts').then((r) => r.data);

export const broadcastUpdate = (id: string, payload: UpdateBroadcastPayload) =>
  apiClient.patch<Broadcast>(`/broadcasts/${id}`, payload).then((r) => r.data);

export const broadcastRevoke = (id: string) =>
  apiClient.patch<Broadcast>(`/broadcasts/${id}/revoke`).then((r) => r.data);

export const broadcastDelete = (id: string) =>
  apiClient.delete<void>(`/broadcasts/${id}`).then((r) => r.data);

// ========== 用户接口 ==========
export const broadcastListMine = () =>
  apiClient.get<MyBroadcastItem[]>('/me/broadcasts').then((r) => r.data);

export const broadcastListUnread = () =>
  apiClient.get<Broadcast[]>('/me/broadcasts/unread').then((r) => r.data);

export const broadcastMarkRead = (id: string) =>
  apiClient.post<void>(`/me/broadcasts/${id}/read`).then((r) => r.data);
